import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ActivityIndicator, FlatList } from 'react-native';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ListsManager from '../services/ListsManager';

const NotificationSettingsScreen = ({ onClose, isModal, ...props }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'he';
  const state = location.state || {};

  const listId = props.listId || state.listId;
  const listName = props.listName || state.listName;
  const notificationMode = props.notificationMode || state.notificationMode;
  const notificationFrequency = props.notificationFrequency || state.notificationFrequency;

  // Internal keys used for logic; labels are translated for display
  const options = ["Mute", "Every Tweet", "Interval"];
  const [selectedOption, setSelectedOption] = useState("Mute");
  const [selectedHours, setSelectedHours] = useState(0);
  const [selectedMinutes, setSelectedMinutes] = useState(5);
  const [showPicker, setShowPicker] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Initialize settings based on passed values
  useEffect(() => {
    if (notificationMode) {
      let mode = notificationMode.toLowerCase();
      console.log("Received notification mode:", mode);

      if (mode === "every_tweet") {
        setSelectedOption("Every Tweet");
      } else if (mode === "interval") {
        setSelectedOption("Interval");
        setShowPicker(true);

        // Parse the frequency if available (e.g., "01:30:00")
        if (notificationFrequency) {
          try {
            const parts = notificationFrequency.split(":");
            if (parts.length >= 2) {
              // Convert string parts to numbers safely
              const hours = parseInt(parts[0], 10);
              const minutes = parseInt(parts[1], 10);

              if (!isNaN(hours)) {
                setSelectedHours(hours);
              }

              if (!isNaN(minutes)) {
                setSelectedMinutes(minutes);
              }
            }
          } catch (error) {
            console.error("Error parsing notification frequency:", error);
          }
        }
      } else {
        // Default to "Mute" for any other value
        setSelectedOption("Mute");
      }
    }
  }, [notificationMode, notificationFrequency]);

  const handleSave = async () => {
    try {
      setIsLoading(true);
      setSaveError(null);

      // Convert the display option to backend format
      let mode;
      let frequency = null;

      // Map UI options to exact backend expected values
      if (selectedOption === "Mute") {
        mode = "mute";
      } else if (selectedOption === "Every Tweet") {
        mode = "every_tweet";
      } else if (selectedOption === "Interval") {
        mode = "interval";

        // Format the time interval in the expected format (HH:MM:00)
        const hoursStr = String(selectedHours).padStart(2, '0');
        const minutesStr = String(selectedMinutes).padStart(2, '0');
        frequency = `${hoursStr}:${minutesStr}:00`;
      }

      console.log("Sending notification settings:", { mode, frequency });

      // Update notification settings
      const success = await ListsManager.updateNotificationSettings(
        listId,
        mode,
        frequency
      );

      if (success) {
        if (onClose) {
          onClose({
            refreshSettings: true,
            notificationMode: mode,
            notificationFrequency: frequency
          });
        } else {
          // Navigate back with refresh flag to update list details
          navigate(-1, {
            state: {
              refreshSettings: true,
              notificationMode: mode,
              notificationFrequency: frequency
            }
          });
        }
      } else {
        setSaveError(t('app.notifications.saveError'));
      }
    } catch (error) {
      console.error('Error saving notification settings:', error);
      setSaveError(t('app.notifications.saveErrorGeneric'));
    } finally {
      setIsLoading(false);
    }
  };

  const renderTimePicker = () => {
    return (
      <View style={styles.pickerContainer}>
        <Text style={styles.pickerLabel}>{t('app.notifications.setInterval')}</Text>
        <View style={styles.pickerRow}>
          <View style={styles.pickerSection}>
            <Text style={styles.pickerTitle}>{t('app.notifications.hours')}</Text>
            <View style={styles.pickerControl}>
              <TouchableOpacity
                style={styles.pickerButton}
                onPress={() => setSelectedHours(Math.max(0, selectedHours - 1))}
              >
                <Text style={styles.pickerButtonText}>{t('app.notifications.decrement')}</Text>
              </TouchableOpacity>
              <Text style={styles.pickerValue}>{selectedHours}</Text>
              <TouchableOpacity
                style={styles.pickerButton}
                onPress={() => setSelectedHours(Math.min(23, selectedHours + 1))}
              >
                <Text style={styles.pickerButtonText}>{t('app.notifications.increment')}</Text>
              </TouchableOpacity>
            </View>
          </View>

          <View style={styles.pickerSection}>
            <Text style={styles.pickerTitle}>{t('app.notifications.minutes')}</Text>
            <View style={styles.pickerControl}>
              <TouchableOpacity
                style={styles.pickerButton}
                onPress={() => setSelectedMinutes(Math.max(0, selectedMinutes - 5))}
              >
                <Text style={styles.pickerButtonText}>{t('app.notifications.decrement')}</Text>
              </TouchableOpacity>
              <Text style={styles.pickerValue}>{selectedMinutes}</Text>
              <TouchableOpacity
                style={styles.pickerButton}
                onPress={() => setSelectedMinutes(Math.min(55, selectedMinutes + 5))}
              >
                <Text style={styles.pickerButtonText}>{t('app.notifications.increment')}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </View>
    );
  };

  const optionLabels = {
    "Mute": t('app.notifications.mute'),
    "Every Tweet": t('app.notifications.everyTweet'),
    "Interval": t('app.notifications.interval'),
  };

  return (
    <View style={styles.container}>
      {!isModal && (
        <View style={styles.header}>
          <TouchableOpacity onPress={() => navigate(-1)}>
            <Text style={[styles.backButton, { transform: [{ scaleX: isRTL ? -1 : 1 }] }]}>← Back</Text>
          </TouchableOpacity>
          <Text style={styles.headerTitle}>
            {listName ? t('app.notifications.title', { listName }) : t('app.notifications.titleFallback')}
          </Text>
          <View style={{ width: 50 }} />
        </View>
      )}

      <Text style={styles.headerText}>
        {t('app.notifications.chooseFrequency', { listName })}
      </Text>

      {/* Notification Options */}
      <FlatList
        data={options}
        keyExtractor={(item) => item}
        style={styles.optionsList}
        renderItem={({ item }) => (
          <TouchableOpacity
            style={[styles.optionItem, selectedOption === item && styles.selectedOptionItem]}
            onPress={() => {
              setSelectedOption(item);
              setShowPicker(item === "Interval");
            }}
          >
            <Text style={styles.optionText}>{optionLabels[item]}</Text>
            {selectedOption === item && <Text style={styles.checkmark}>✓</Text>}
          </TouchableOpacity>
        )}
      />

      {/* Interval Picker */}
      {showPicker && renderTimePicker()}

      {saveError && (
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>{saveError}</Text>
        </View>
      )}

      {/* Action Buttons */}
      <View style={styles.buttonContainer}>
        <TouchableOpacity
          style={styles.cancelButton}
          onPress={() => onClose ? onClose() : navigate(-1)}
          disabled={isLoading}
        >
          <Text style={styles.buttonText}>{t('app.common.cancel')}</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={styles.saveButton}
          onPress={handleSave}
          disabled={isLoading}
        >
          {isLoading ? (
            <ActivityIndicator size="small" color="white" />
          ) : (
            <Text style={styles.buttonText}>{t('app.common.save')}</Text>
          )}
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'var(--bg-deep-void)',
    backgroundImage: 'radial-gradient(circle at 50% 0%, rgba(59, 130, 246, 0.15), transparent 50%)',
    padding: 24,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 32,
    backgroundColor: 'var(--bg-card)',
    padding: 16,
    borderRadius: 20,
    boxShadow: 'var(--shadow-glass)',
    backdropFilter: 'blur(12px)',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.08)',
  },
  backButton: {
    color: 'var(--text-primary)',
    fontSize: 14,
    fontWeight: '600',
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 12,
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.1)',
  },
  headerTitle: {
    color: 'var(--text-primary)',
    fontSize: 18,
    fontWeight: '700',
    fontFamily: 'Outfit, sans-serif',
  },
  headerText: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 32,
    color: 'var(--text-secondary)',
    lineHeight: 24,
    paddingHorizontal: 16,
  },
  optionsList: {
    marginBottom: 32,
  },
  optionItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 20,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.08)',
    backgroundColor: 'var(--bg-card)',
    marginBottom: 12,
    borderRadius: 16,
    alignItems: 'center',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    transition: 'all 0.2s cubic-bezier(0.25, 0.8, 0.25, 1)',
    cursor: 'pointer',
  },
  selectedOptionItem: {
    backgroundColor: 'rgba(59, 130, 246, 0.15)',
    borderColor: 'var(--accent-primary)',
    boxShadow: '0 0 15px rgba(59, 130, 246, 0.2)',
  },
  optionText: {
    fontSize: 16,
    color: 'var(--text-primary)',
    fontWeight: '500',
  },
  checkmark: {
    fontSize: 18,
    color: 'var(--accent-primary)',
    fontWeight: 'bold',
  },
  pickerContainer: {
    marginTop: 20,
    padding: 24,
    backgroundColor: 'var(--bg-card)',
    borderRadius: 20,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.08)',
    boxShadow: 'var(--shadow-glass)',
    backdropFilter: 'blur(12px)',
  },
  pickerLabel: {
    fontSize: 16,
    color: 'var(--text-primary)',
    marginBottom: 16,
    fontWeight: '600',
    textAlign: 'center',
  },
  pickerRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 32,
  },
  pickerSection: {
    flex: 1,
    alignItems: 'center',
    maxWidth: 160,
  },
  pickerTitle: {
    color: 'var(--text-secondary)',
    marginBottom: 12,
    textTransform: 'uppercase',
    fontSize: 12,
    fontWeight: '600',
    letterSpacing: '0.05em',
  },
  pickerControl: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    padding: 8,
    backgroundColor: 'var(--bg-card)',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.05)',
  },
  pickerButton: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.1)',
    transition: 'all 0.2s',
    cursor: 'pointer',
  },
  pickerButtonText: {
    color: 'var(--text-primary)',
    fontSize: 20,
    fontWeight: '400',
  },
  pickerValue: {
    color: 'var(--text-primary)',
    fontSize: 24,
    fontWeight: '700',
    fontFamily: 'Outfit, sans-serif',
  },
  errorContainer: {
    padding: 16,
    marginVertical: 16,
    backgroundColor: 'rgba(244, 63, 94, 0.1)',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: 'rgba(244, 63, 94, 0.3)',
    alignItems: 'center',
  },
  errorText: {
    color: 'var(--accent-warning)',
    fontWeight: '500',
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 'auto',
    marginBottom: 20,
    gap: 16,
  },
  cancelButton: {
    flex: 1,
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    padding: 16,
    borderRadius: 16,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.1)',
    transition: 'all 0.2s',
    cursor: 'pointer',
  },
  saveButton: {
    flex: 1,
    backgroundColor: 'var(--accent-primary)',
    padding: 16,
    borderRadius: 16,
    alignItems: 'center',
    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)',
    transition: 'all 0.2s',
    cursor: 'pointer',
  },
  buttonText: {
    color: 'white',
    fontWeight: '600',
    fontSize: 16,
    letterSpacing: '0.02em',
  },
});

export default NotificationSettingsScreen; 